import { open } from '@wix/communication-channel';
import { getMonitoringClientFunction } from '@wix/monitoring-browser-sdk-host';
import { CreateHost, ProviderAPI, OnBeforeUnload, GetLayout } from './types';
import { registerHeightReporter } from './helpers/registerHeightReporter';
import { isWixOrigin } from './helpers/isWixOrigin';
import { registerBeforeUnloadHandler } from './helpers/registerOnBeforeUnload';
import { getEssentialsFromQueryParams } from './helpers/essentials';
import { GetSiteInfo } from './types/getSiteInfo';
import { showError } from './helpers/showError';
import {
  parseEncodedJson,
  parsePageLayoutFromUrl,
} from './helpers/queryParamsParsing';

declare global {
  interface Window {
    Sentry?: any;
  }
}

export const createHost: CreateHost = function (config = {}) {
  // when full url is provided (worker case) use it and ignore own location search
  const searchString =
    (config.fullUrl && new URL(config.fullUrl).search) ||
    (typeof window !== 'undefined' && window.location.search) ||
    '';
  const queryParams = new URLSearchParams(searchString);
  const origin = config.origin || queryParams.get('origin') || '';
  const postMessage =
    config.postMessage || window.parent.postMessage.bind(window.parent);

  if (!isWixOrigin(origin)) {
    throw new Error(
      `Wix Dashboard SDK: Unable to establish a connection with the Wix dashboard. Please ensure that you are running your app within the Wix dashboard.`,
    );
  }

  const { channel, close } = open<ProviderAPI>({ postMessage, origin });
  const closeMethods = [close];

  if (config.autoHeightUpdate === undefined || config.autoHeightUpdate) {
    const element = config.autoHeightElement || document.documentElement;
    const clearHeightReport = registerHeightReporter(channel, element);
    closeMethods.push(() => clearHeightReport());
  }

  const { onBeforeUnload, clearBeforeUnloadHandler } =
    registerBeforeUnloadHandler(channel);
  closeMethods.push(clearBeforeUnloadHandler);

  const siteInfo = parseEncodedJson(
    queryParams.get('siteInfo') || '',
    'siteInfo',
  );
  const getSiteInfoSync: GetSiteInfo = () => siteInfo;

  const pageLayout = parsePageLayoutFromUrl(queryParams);
  const getLayoutSync: GetLayout = () => pageLayout;

  const channelProxy = new Proxy(channel, {
    get(target, prop, receiver) {
      if (prop === 'onBeforeUnload') {
        return onBeforeUnload;
      }
      if (prop === 'getSiteInfo') {
        return getSiteInfoSync;
      }
      if (prop === 'pageGetLayout') {
        return getLayoutSync;
      }
      return Reflect.get(target, prop, receiver);
    },
  }) as typeof channel & {
    onBeforeUnload: OnBeforeUnload;
    getSiteInfo: GetSiteInfo; // keeping it sync
    pageGetLayout: GetLayout; // synthesized client-side via Proxy
  } & Omit<typeof channel, 'openItemSelector'>;

  const { monitoring, ...essentials } =
    getEssentialsFromQueryParams(queryParams) || {};

  const getMonitoringClient = getMonitoringClientFunction({
    hostContext: monitoring?.context,
    monitoringConfig: monitoring?.config,
    sentrySDK: typeof window !== 'undefined' ? window.Sentry : undefined,
  });

  const workspace = {
    openItemSelector: channel.openItemSelector,
    apps: {
      startInstallFlow: channel.startInstallFlow,
      startPurchasePlanFlow: channel.startPurchasePlanFlow,
      startUpdateFlow: channel.startUpdateFlow,
      openContactSupport: channel.openContactSupport,
    },
    credits: {
      openPaywallModal: channel.openPaywallModal,
    },
    tools: {
      startInstallFlow: channel.toolsStartInstallFlow,
      exploreTools: channel.toolsExploreTools,
    },
  };

  return {
    channel: channelProxy,
    close: () => closeMethods.forEach((method) => method()),
    getMonitoringClient,
    essentials,
    workspace,
    showError: showError(channel),
  };
};
